import { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Link } from 'react-router-dom';
import { Calculator, Anchor, DollarSign, Info, Ship, Tag } from 'lucide-react';
import axios from 'axios';
import { usePageTitle } from '../hooks/usePageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CharterCalculator() {
  usePageTitle('Yacht Calculator');
  
  const [activeTab, setActiveTab] = useState('charter'); // 'charter' or 'price'
  const [yachts, setYachts] = useState([]);
  const [allYachts, setAllYachts] = useState([]);
  const [selectedYacht, setSelectedYacht] = useState(null);
  
  // Charter Calculator State
  const [charterInputs, setCharterInputs] = useState({
    duration: 7,
    guests: 8,
    cruisingDistance: 200,
    includeAPA: true,
    apaPercentage: 30,
    gratuityPercentage: 15,
    includeDocking: false,
    dockingFees: 500,
  });
  const [charterResults, setCharterResults] = useState(null);

  // Price Estimator State
  const [priceInputs, setPriceInputs] = useState({
    yachtType: 'motor',
    length: 80,
    year: 2020,
    builder: 'standard',
    condition: 'excellent',
    hoursOrMiles: 500,
    refit: 'none',
    location: 'asia',
  });
  const [priceResults, setPriceResults] = useState(null);

  useEffect(() => {
    fetchYachts();
    fetchAllYachts();
  }, []);

  useEffect(() => {
    if (selectedYacht && activeTab === 'charter') {
      calculateCharterCosts();
    }
  }, [selectedYacht, charterInputs, activeTab]);

  useEffect(() => {
    if (activeTab === 'price') {
      calculateYachtPrice();
    }
  }, [priceInputs, activeTab]);

  const fetchYachts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/yachts?listing_type=charter`);
      setYachts(response.data);
      if (response.data.length > 0) {
        setSelectedYacht(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching yachts:', error);
    }
  };

  const fetchAllYachts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/yachts`);
      setAllYachts(response.data);
    } catch (error) {
      console.error('Error fetching all yachts:', error);
    }
  };

  const calculateCharterCosts = () => {
    if (!selectedYacht || !selectedYacht.charter_weekly_usd) return;

    const weeklyRate = selectedYacht.charter_weekly_usd;
    const weeks = charterInputs.duration / 7;
    const baseCharter = weeklyRate * weeks;
    const apa = charterInputs.includeAPA ? baseCharter * (charterInputs.apaPercentage / 100) : 0;
    const gratuity = baseCharter * (charterInputs.gratuityPercentage / 100);
    const docking = charterInputs.includeDocking ? charterInputs.dockingFees * charterInputs.duration : 0;
    const total = baseCharter + apa + gratuity + docking;

    setCharterResults({
      baseCharter,
      apa,
      gratuity,
      docking,
      total,
      perDay: total / charterInputs.duration,
      perGuest: total / charterInputs.guests,
    });
  };

  const calculateYachtPrice = () => {
    // Base price per foot by yacht type
    const basePricePerFoot = {
      motor: 45000,
      sailing: 35000,
      catamaran: 55000,
      superyacht: 120000,
    };

    // Builder multiplier
    const builderMultiplier = {
      budget: 0.7,
      standard: 1.0,
      premium: 1.4,
      luxury: 2.0,
    };

    // Condition multiplier
    const conditionMultiplier = {
      excellent: 1.0,
      good: 0.85,
      fair: 0.7,
      project: 0.5,
    };

    // Age depreciation (per year from new)
    const currentYear = new Date().getFullYear();
    const age = currentYear - priceInputs.year;
    const ageDepreciation = Math.max(0.4, 1 - (age * 0.03)); // 3% per year, min 40%

    // Hours/miles depreciation
    const usageDepreciation = Math.max(0.7, 1 - (priceInputs.hoursOrMiles / 10000)); // Max 30% reduction

    // Refit value addition
    const refitAddition = {
      none: 0,
      partial: 0.1,
      full: 0.25,
    };

    // Location multiplier
    const locationMultiplier = {
      asia: 0.9,
      mediterranean: 1.1,
      caribbean: 1.0,
      usa: 1.05,
    };

    // Calculate base price
    let basePrice = basePricePerFoot[priceInputs.yachtType] * priceInputs.length;
    
    // Apply multipliers
    basePrice *= builderMultiplier[priceInputs.builder];
    basePrice *= conditionMultiplier[priceInputs.condition];
    basePrice *= ageDepreciation;
    basePrice *= usageDepreciation;
    basePrice *= locationMultiplier[priceInputs.location];
    
    // Add refit value
    const refitValue = basePrice * refitAddition[priceInputs.refit];
    basePrice += refitValue;

    // Calculate ranges (±15%)
    const lowEstimate = basePrice * 0.85;
    const highEstimate = basePrice * 1.15;

    // Find similar yachts from listings
    const similarYachts = allYachts.filter(yacht => {
      const lengthMatch = Math.abs((yacht.length_ft || 0) - priceInputs.length) <= 20;
      const typeMatch = yacht.yacht_type?.toLowerCase().includes(priceInputs.yachtType) || 
                       (priceInputs.yachtType === 'motor' && yacht.yacht_type?.toLowerCase().includes('power'));
      return lengthMatch && typeMatch && yacht.price_usd;
    }).slice(0, 3);

    setPriceResults({
      estimatedPrice: basePrice,
      lowEstimate,
      highEstimate,
      ageDepreciation: (1 - ageDepreciation) * 100,
      usageDepreciation: (1 - usageDepreciation) * 100,
      refitValue,
      similarYachts,
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto pb-24">
      {/* Header */}
      <div className="mb-12 text-center">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Calculator className="text-gold" size={48} />
        </div>
        <h1
          className="text-5xl md:text-6xl font-medium tracking-tight mb-4"
          style={{ fontFamily: 'Playfair Display, serif' }}
          data-testid="calculator-title"
        >
          Yacht <span className="text-gold">Calculator</span>
        </h1>
        <p className="text-lg text-white/60 max-w-2xl mx-auto">
          Estimate charter costs or calculate the market value of a yacht. Make informed decisions with our comprehensive calculators.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex justify-center mb-12">
        <div className="inline-flex bg-white/5 p-1 rounded-sm" data-testid="calculator-tabs">
          <button
            onClick={() => setActiveTab('charter')}
            className={`px-8 py-4 text-sm uppercase tracking-wider font-medium transition-all ${
              activeTab === 'charter'
                ? 'bg-gold text-[#0A192F]'
                : 'text-white/70 hover:text-white'
            }`}
            data-testid="charter-tab"
          >
            <Ship size={18} className="inline mr-2" />
            Charter Cost
          </button>
          <button
            onClick={() => setActiveTab('price')}
            className={`px-8 py-4 text-sm uppercase tracking-wider font-medium transition-all ${
              activeTab === 'price'
                ? 'bg-gold text-[#0A192F]'
                : 'text-white/70 hover:text-white'
            }`}
            data-testid="price-tab"
          >
            <Tag size={18} className="inline mr-2" />
            Price Estimator
          </button>
        </div>
      </div>

      {/* Charter Calculator */}
      {activeTab === 'charter' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Calculator Form */}
          <div className="glass-light p-8 lg:p-10" data-testid="charter-form">
            <h2 className="text-2xl font-medium mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
              Trip Details
            </h2>

            <div className="space-y-6">
              {/* Yacht Selection */}
              <div>
                <Label className="text-white/80 mb-2 block">Select Yacht</Label>
                <Select
                  value={selectedYacht?.yacht_id}
                  onValueChange={(value) => {
                    const yacht = yachts.find((y) => y.yacht_id === value);
                    setSelectedYacht(yacht);
                  }}
                >
                  <SelectTrigger className="bg-transparent border-white/20 text-white" data-testid="yacht-select">
                    <SelectValue placeholder="Choose a yacht" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A192F] border-white/20 text-white max-h-[300px]">
                    {yachts.map((yacht) => (
                      <SelectItem key={yacht.yacht_id} value={yacht.yacht_id}>
                        {yacht.name} - {formatCurrency(yacht.charter_weekly_usd)}/week
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Duration Slider */}
              <div>
                <Label className="text-white/80 mb-3 block">
                  Charter Duration: <span className="text-gold font-semibold">{charterInputs.duration} days</span>
                </Label>
                <Slider
                  value={[charterInputs.duration]}
                  onValueChange={(value) => setCharterInputs({ ...charterInputs, duration: value[0] })}
                  min={1}
                  max={30}
                  step={1}
                  className="w-full"
                  data-testid="duration-slider"
                />
                <div className="flex justify-between text-xs text-white/40 mt-2">
                  <span>1 day</span>
                  <span>30 days</span>
                </div>
              </div>

              {/* Number of Guests */}
              <div>
                <Label className="text-white/80 mb-3 block">
                  Number of Guests: <span className="text-gold font-semibold">{charterInputs.guests}</span>
                </Label>
                <Slider
                  value={[charterInputs.guests]}
                  onValueChange={(value) => setCharterInputs({ ...charterInputs, guests: value[0] })}
                  min={1}
                  max={selectedYacht?.guest_capacity || 12}
                  step={1}
                  className="w-full"
                  data-testid="guests-slider"
                />
                <div className="flex justify-between text-xs text-white/40 mt-2">
                  <span>1 guest</span>
                  <span>{selectedYacht?.guest_capacity || 12} guests</span>
                </div>
              </div>

              {/* APA Percentage */}
              <div>
                <Label className="text-white/80 mb-3 flex items-center gap-2">
                  APA (Advance Provisioning):
                  <span className="text-gold font-semibold">{charterInputs.apaPercentage}%</span>
                  <Info size={14} className="text-white/40" />
                </Label>
                <Slider
                  value={[charterInputs.apaPercentage]}
                  onValueChange={(value) => setCharterInputs({ ...charterInputs, apaPercentage: value[0] })}
                  min={20}
                  max={40}
                  step={5}
                  className="w-full"
                  data-testid="apa-slider"
                />
                <p className="text-xs text-white/40 mt-2">
                  Covers fuel, provisions, docking fees, and port expenses (industry standard 25-35%)
                </p>
              </div>

              {/* Gratuity Percentage */}
              <div>
                <Label className="text-white/80 mb-3 block">
                  Crew Gratuity: <span className="text-gold font-semibold">{charterInputs.gratuityPercentage}%</span>
                </Label>
                <Slider
                  value={[charterInputs.gratuityPercentage]}
                  onValueChange={(value) => setCharterInputs({ ...charterInputs, gratuityPercentage: value[0] })}
                  min={10}
                  max={20}
                  step={5}
                  className="w-full"
                  data-testid="gratuity-slider"
                />
                <p className="text-xs text-white/40 mt-2">Industry standard: 10-20% of base charter rate</p>
              </div>

              {/* Additional Options */}
              <div className="pt-4 border-t border-white/10">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={charterInputs.includeDocking}
                    onChange={(e) => setCharterInputs({ ...charterInputs, includeDocking: e.target.checked })}
                    className="w-4 h-4"
                    data-testid="docking-checkbox"
                  />
                  <span className="text-white/80">Include additional marina/docking fees</span>
                </label>
                {charterInputs.includeDocking && (
                  <Input
                    type="number"
                    value={charterInputs.dockingFees}
                    onChange={(e) => setCharterInputs({ ...charterInputs, dockingFees: parseInt(e.target.value) || 0 })}
                    className="bg-transparent border-white/20 text-white mt-3"
                    placeholder="Per day ($)"
                    data-testid="docking-input"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Charter Results Panel */}
          <div>
            {selectedYacht && charterResults && (
              <div className="glass-heavy p-8 lg:p-10 sticky top-32" data-testid="charter-results">
                <h2 className="text-2xl font-medium mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Estimated Cost Breakdown
                </h2>

                {/* Selected Yacht Info */}
                <div className="mb-8 pb-6 border-b border-white/10">
                  <p className="text-xs uppercase tracking-wide text-gold mb-2">{selectedYacht.builder}</p>
                  <h3 className="text-xl font-medium mb-2">{selectedYacht.name}</h3>
                  <p className="text-white/60 text-sm">
                    {selectedYacht.length_ft}ft • {selectedYacht.guest_capacity} guests • {selectedYacht.location}
                  </p>
                </div>

                {/* Cost Breakdown */}
                <div className="space-y-4 mb-8">
                  <div className="flex justify-between items-center">
                    <span className="text-white/70">Base Charter Rate</span>
                    <span className="font-semibold">{formatCurrency(charterResults.baseCharter)}</span>
                  </div>
                  {charterInputs.includeAPA && (
                    <div className="flex justify-between items-center">
                      <span className="text-white/70">APA ({charterInputs.apaPercentage}%)</span>
                      <span className="font-semibold">{formatCurrency(charterResults.apa)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-white/70">Crew Gratuity ({charterInputs.gratuityPercentage}%)</span>
                    <span className="font-semibold">{formatCurrency(charterResults.gratuity)}</span>
                  </div>
                  {charterInputs.includeDocking && charterResults.docking > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-white/70">Marina/Docking Fees</span>
                      <span className="font-semibold">{formatCurrency(charterResults.docking)}</span>
                    </div>
                  )}
                </div>

                {/* Total */}
                <div className="pt-6 border-t border-gold/30 mb-8">
                  <div className="flex justify-between items-center mb-6">
                    <span className="text-xl font-medium">Estimated Total</span>
                    <span className="text-3xl font-semibold text-gold">{formatCurrency(charterResults.total)}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="glass-light p-3 text-center">
                      <p className="text-white/60 mb-1">Per Day</p>
                      <p className="font-semibold">{formatCurrency(charterResults.perDay)}</p>
                    </div>
                    <div className="glass-light p-3 text-center">
                      <p className="text-white/60 mb-1">Per Guest</p>
                      <p className="font-semibold">{formatCurrency(charterResults.perGuest)}</p>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <Link to={`/yacht/${selectedYacht.yacht_id}`}>
                  <Button
                    className="w-full bg-gold text-[#0A192F] hover:bg-[#E5C55D] py-6 uppercase tracking-widest rounded-none"
                    data-testid="inquire-btn"
                  >
                    <DollarSign size={18} className="mr-2" />
                    Inquire About {selectedYacht.name}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Price Estimator */}
      {activeTab === 'price' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Price Form */}
          <div className="glass-light p-8 lg:p-10" data-testid="price-form">
            <h2 className="text-2xl font-medium mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
              Yacht Specifications
            </h2>

            <div className="space-y-6">
              {/* Yacht Type */}
              <div>
                <Label className="text-white/80 mb-2 block">Yacht Type</Label>
                <Select
                  value={priceInputs.yachtType}
                  onValueChange={(value) => setPriceInputs({ ...priceInputs, yachtType: value })}
                >
                  <SelectTrigger className="bg-transparent border-white/20 text-white" data-testid="type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A192F] border-white/20 text-white">
                    <SelectItem value="motor">Motor Yacht</SelectItem>
                    <SelectItem value="sailing">Sailing Yacht</SelectItem>
                    <SelectItem value="catamaran">Catamaran</SelectItem>
                    <SelectItem value="superyacht">Superyacht (100ft+)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Length */}
              <div>
                <Label className="text-white/80 mb-3 block">
                  Length: <span className="text-gold font-semibold">{priceInputs.length} ft</span>
                </Label>
                <Slider
                  value={[priceInputs.length]}
                  onValueChange={(value) => setPriceInputs({ ...priceInputs, length: value[0] })}
                  min={30}
                  max={200}
                  step={5}
                  className="w-full"
                  data-testid="length-slider"
                />
                <div className="flex justify-between text-xs text-white/40 mt-2">
                  <span>30 ft</span>
                  <span>200 ft</span>
                </div>
              </div>

              {/* Year Built */}
              <div>
                <Label className="text-white/80 mb-3 block">
                  Year Built: <span className="text-gold font-semibold">{priceInputs.year}</span>
                </Label>
                <Slider
                  value={[priceInputs.year]}
                  onValueChange={(value) => setPriceInputs({ ...priceInputs, year: value[0] })}
                  min={1990}
                  max={2025}
                  step={1}
                  className="w-full"
                  data-testid="year-slider"
                />
                <div className="flex justify-between text-xs text-white/40 mt-2">
                  <span>1990</span>
                  <span>2025</span>
                </div>
              </div>

              {/* Builder Tier */}
              <div>
                <Label className="text-white/80 mb-2 block">Builder / Brand Tier</Label>
                <Select
                  value={priceInputs.builder}
                  onValueChange={(value) => setPriceInputs({ ...priceInputs, builder: value })}
                >
                  <SelectTrigger className="bg-transparent border-white/20 text-white" data-testid="builder-select">
                    <SelectValue placeholder="Select tier" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A192F] border-white/20 text-white">
                    <SelectItem value="budget">Budget (Regional builders)</SelectItem>
                    <SelectItem value="standard">Standard (Beneteau, Jeanneau)</SelectItem>
                    <SelectItem value="premium">Premium (Sunseeker, Princess)</SelectItem>
                    <SelectItem value="luxury">Luxury (Feadship, Lürssen)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Condition */}
              <div>
                <Label className="text-white/80 mb-2 block">Condition</Label>
                <Select
                  value={priceInputs.condition}
                  onValueChange={(value) => setPriceInputs({ ...priceInputs, condition: value })}
                >
                  <SelectTrigger className="bg-transparent border-white/20 text-white" data-testid="condition-select">
                    <SelectValue placeholder="Select condition" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A192F] border-white/20 text-white">
                    <SelectItem value="excellent">Excellent (Like new)</SelectItem>
                    <SelectItem value="good">Good (Well maintained)</SelectItem>
                    <SelectItem value="fair">Fair (Needs work)</SelectItem>
                    <SelectItem value="project">Project (Major refit needed)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Engine Hours */}
              <div>
                <Label className="text-white/80 mb-3 block">
                  Engine Hours: <span className="text-gold font-semibold">{priceInputs.hoursOrMiles.toLocaleString()} hrs</span>
                </Label>
                <Slider
                  value={[priceInputs.hoursOrMiles]}
                  onValueChange={(value) => setPriceInputs({ ...priceInputs, hoursOrMiles: value[0] })}
                  min={0}
                  max={5000}
                  step={100}
                  className="w-full"
                  data-testid="hours-slider"
                />
                <div className="flex justify-between text-xs text-white/40 mt-2">
                  <span>0 hrs</span>
                  <span>5,000 hrs</span>
                </div>
              </div>

              {/* Recent Refit */}
              <div>
                <Label className="text-white/80 mb-2 block">Recent Refit</Label>
                <Select
                  value={priceInputs.refit}
                  onValueChange={(value) => setPriceInputs({ ...priceInputs, refit: value })}
                >
                  <SelectTrigger className="bg-transparent border-white/20 text-white" data-testid="refit-select">
                    <SelectValue placeholder="Select refit status" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A192F] border-white/20 text-white">
                    <SelectItem value="none">No recent refit</SelectItem>
                    <SelectItem value="partial">Partial refit (last 3 years)</SelectItem>
                    <SelectItem value="full">Full refit (last 3 years)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Location */}
              <div>
                <Label className="text-white/80 mb-2 block">Current Location</Label>
                <Select
                  value={priceInputs.location}
                  onValueChange={(value) => setPriceInputs({ ...priceInputs, location: value })}
                >
                  <SelectTrigger className="bg-transparent border-white/20 text-white" data-testid="location-select">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A192F] border-white/20 text-white">
                    <SelectItem value="asia">Southeast Asia</SelectItem>
                    <SelectItem value="mediterranean">Mediterranean</SelectItem>
                    <SelectItem value="caribbean">Caribbean</SelectItem>
                    <SelectItem value="usa">USA / Americas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Price Results Panel */}
          <div>
            {priceResults && (
              <div className="glass-heavy p-8 lg:p-10 sticky top-32" data-testid="price-results">
                <h2 className="text-2xl font-medium mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Estimated Market Value
                </h2>

                {/* Main Estimate */}
                <div className="text-center mb-8 pb-8 border-b border-white/10">
                  <p className="text-white/60 mb-2">Estimated Value</p>
                  <p className="text-4xl md:text-5xl font-semibold text-gold mb-4">
                    {formatCurrency(priceResults.estimatedPrice)}
                  </p>
                  <p className="text-white/60 text-sm">
                    Range: {formatCurrency(priceResults.lowEstimate)} - {formatCurrency(priceResults.highEstimate)}
                  </p>
                </div>

                {/* Value Factors */}
                <div className="space-y-4 mb-8">
                  <h3 className="text-lg font-medium mb-4">Value Factors</h3>
                  <div className="flex justify-between items-center">
                    <span className="text-white/70">Base Value ({priceInputs.length}ft {priceInputs.yachtType})</span>
                    <span className="font-semibold text-green-400">+100%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/70">Age Depreciation ({new Date().getFullYear() - priceInputs.year} years)</span>
                    <span className="font-semibold text-red-400">-{priceResults.ageDepreciation.toFixed(0)}%</span>
                  </div>
                  {priceResults.usageDepreciation > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-white/70">Usage ({priceInputs.hoursOrMiles.toLocaleString()} hrs)</span>
                      <span className="font-semibold text-red-400">-{priceResults.usageDepreciation.toFixed(0)}%</span>
                    </div>
                  )}
                  {priceResults.refitValue > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-white/70">Recent Refit Value</span>
                      <span className="font-semibold text-green-400">+{formatCurrency(priceResults.refitValue)}</span>
                    </div>
                  )}
                </div>

                {/* Similar Listings */}
                {priceResults.similarYachts.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-medium mb-4">Similar Yachts in Our Listings</h3>
                    <div className="space-y-3">
                      {priceResults.similarYachts.map((yacht) => (
                        <Link
                          key={yacht.yacht_id}
                          to={`/yacht/${yacht.yacht_id}`}
                          className="block glass-light p-4 hover:bg-white/10 transition-colors"
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-medium">{yacht.name}</p>
                              <p className="text-white/60 text-sm">{yacht.length_ft}ft • {yacht.year}</p>
                            </div>
                            <p className="text-gold font-semibold">{formatCurrency(yacht.price_usd)}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* CTA */}
                <Link to="/listings?listing_type=sale">
                  <Button
                    className="w-full bg-gold text-[#0A192F] hover:bg-[#E5C55D] py-6 uppercase tracking-widest rounded-none"
                    data-testid="browse-sale-btn"
                  >
                    <Anchor size={18} className="mr-2" />
                    Browse Yachts for Sale
                  </Button>
                </Link>

                <p className="text-white/40 text-xs text-center mt-4">
                  * This is an estimate only. Actual market value depends on many factors. Contact us for a professional valuation.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Information Section */}
      <div className="mt-20 glass-light p-10">
        <h3 className="text-2xl font-medium mb-6 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
          {activeTab === 'charter' ? 'Understanding Charter Costs' : 'Understanding Yacht Valuation'}
        </h3>
        {activeTab === 'charter' ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-white/70">
            <div>
              <h4 className="text-gold font-semibold mb-3">Base Charter Rate</h4>
              <p className="text-sm leading-relaxed">
                The weekly or daily rate for yacht rental, including crew salaries, vessel insurance, and standard onboard amenities.
              </p>
            </div>
            <div>
              <h4 className="text-gold font-semibold mb-3">APA (25-35%)</h4>
              <p className="text-sm leading-relaxed">
                Advance Provisioning Allowance covers fuel, food, beverages, docking fees, port charges, and special requests. Unused funds are refunded.
              </p>
            </div>
            <div>
              <h4 className="text-gold font-semibold mb-3">Crew Gratuity (10-20%)</h4>
              <p className="text-sm leading-relaxed">
                Industry standard tip for exceptional service, typically 15% of base charter rate, distributed among the crew.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-white/70">
            <div>
              <h4 className="text-gold font-semibold mb-3">Size & Type</h4>
              <p className="text-sm leading-relaxed">
                Length is the primary value driver. Motor yachts and catamarans typically command higher prices per foot than sailing yachts.
              </p>
            </div>
            <div>
              <h4 className="text-gold font-semibold mb-3">Age & Condition</h4>
              <p className="text-sm leading-relaxed">
                Yachts depreciate approximately 3-5% per year. Well-maintained vessels with recent refits retain value better than neglected ones.
              </p>
            </div>
            <div>
              <h4 className="text-gold font-semibold mb-3">Builder Reputation</h4>
              <p className="text-sm leading-relaxed">
                Premium builders like Feadship, Lürssen, and Benetti command significant premiums due to build quality and brand prestige.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
